import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo2 from '../assets/logo2.png';
import {
  ShoppingCart, X,
  LayoutDashboard, UserCircle, Menu, ArrowRight, LogOut,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { to: '/',          label: 'Home'           },
  { to: '/local',     label: 'Local'          },
  { to: '/cambridge', label: 'Cambridge'      },
  { to: '/edexcel',   label: 'Edexcel'        },
  { to: '/tutors',    label: 'Find a Tutor'   },
  { to: '/private',   label: 'Private Classes'},
  { to: '/shop',      label: 'Shop'           },
  { to: '/contact',   label: 'Contact'        },
];

const Navbar = () => {
  const [userInfo,   setUserInfo]  = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,        setScrolled]        = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Sync user + close mobile on route change
  useEffect(() => {
    const stored = localStorage.getItem('userInfo');
    setUserInfo(stored ? JSON.parse(stored) : null);
    setMobileOpen(false);
  }, [location]);

  // Scroll-aware background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('userInfo');
    setUserInfo(null);
    navigate('/login');
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const isAdmin   = userInfo && (userInfo.role === 'admin' || userInfo.email?.toLowerCase() === 'admin@gmail.com');
  const isTeacher = userInfo?.role === 'teacher';
  const isStudent = userInfo?.role === 'student';

  const userName    = userInfo?.firstName || userInfo?.name || 'User';
  const userInitial = userName.charAt(0).toUpperCase();
  const avatarColor = isTeacher ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    : isStudent ? 'bg-accentBlue/20 text-accentBlue border-accentBlue/30'
    : 'bg-white/10 text-gray-300 border-white/20';

  if (
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/teachers/admin')
  ) return null;

  return (
    <>
      {/* ── Main Nav ── */}
      <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-darkBg/90 backdrop-blur-xl border-b border-white/10 shadow-xl shadow-black/20'
          : 'bg-white/3 backdrop-blur-md border-b border-white/8'
      }`}>
        <div className="max-w-7xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-0 shrink-0 group">
            <img
              src={logo2}
              alt="Smart Edu Next"
              className="h-12 w-auto object-contain translate-y-1.5 group-hover:scale-105 transition-transform"
            />
            <span className="text-lg font-extrabold tracking-tighter text-white group-hover:text-accentBlue transition-colors">
              SMART EDU<span className="text-accentBlue group-hover:text-white transition-colors"> NEXT</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to}
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isActive(to)
                    ? 'text-accentBlue bg-accentBlue/8'
                    : 'text-gray-400 hover:text-white hover:bg-white/6'
                }`}>
                {label}
                {isActive(to) && (
                  <motion.div layoutId="nav-active-dot"
                    className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accentBlue"
                  />
                )}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin"
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isActive('/admin') ? 'text-accentBlue bg-accentBlue/8' : 'text-gray-400 hover:text-white hover:bg-white/6'
                }`}>
                Admin
              </Link>
            )}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2.5">
            {/* Cart */}
            <Link to="/cart"
              className="relative p-2 text-gray-400 hover:text-white hover:bg-white/8 rounded-xl transition-all"
              aria-label="Cart">
              <ShoppingCart size={19} />
            </Link>

            {userInfo ? (
              <div className="flex items-center gap-2">
                {/* Role-specific action button */}
                {isTeacher && (
                  <button onClick={() => navigate('/teachers/admin')}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 border border-purple-500/25 text-purple-400 text-xs font-bold rounded-xl hover:bg-purple-500/20 transition-colors">
                    <LayoutDashboard size={13} /> My Admin
                  </button>
                )}
                {isStudent && (
                  <button onClick={() => navigate('/student-profile')}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-accentBlue/10 border border-accentBlue/25 text-accentBlue text-xs font-bold rounded-xl hover:bg-accentBlue/20 transition-colors">
                    <UserCircle size={13} /> My Profile
                  </button>
                )}

                {/* User avatar + name */}
                <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border ${avatarColor} bg-transparent`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${avatarColor}`}>
                    {userInitial}
                  </div>
                  <span className="text-xs font-semibold text-gray-300 max-w-[80px] truncate">{userName}</span>
                </div>

                {/* Logout */}
                <button onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/8 border border-red-500/20 text-red-400 text-xs font-bold rounded-xl hover:bg-red-500/15 transition-colors"
                  title="Logout">
                  <LogOut size={13} />
                  <span className="hidden sm:block">Logout</span>
                </button>
              </div>
            ) : (
              <button onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-4 py-2 bg-accentBlue text-black text-sm font-bold rounded-xl hover:scale-105 transition-transform shadow-md shadow-accentBlue/20">
                Login <ArrowRight size={14} />
              </button>
            )}

            {/* Hamburger */}
            <button onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-white/8 rounded-xl transition-all">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* ── Mobile menu ── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="lg:hidden overflow-hidden border-t border-white/8 bg-darkBg/95 backdrop-blur-xl"
            >
              <div className="max-w-7xl mx-auto px-5 py-4 space-y-1">
                {NAV_LINKS.map(({ to, label }) => (
                  <Link key={to} to={to}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive(to)
                        ? 'bg-accentBlue/10 text-accentBlue border border-accentBlue/20'
                        : 'text-gray-400 hover:bg-white/6 hover:text-white border border-transparent'
                    }`}>
                    {label}
                    {isActive(to) && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accentBlue" />}
                  </Link>
                ))}
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:bg-white/6 hover:text-white border border-transparent transition-all">
                    Admin Panel
                  </Link>
                )}

                {/* Mobile user section */}
                {userInfo && (
                  <div className="pt-3 mt-3 border-t border-white/8 space-y-2">
                    {isTeacher && (
                      <button onClick={() => navigate('/teachers/admin')}
                        className="w-full flex items-center gap-2 px-4 py-3 bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-bold rounded-xl hover:bg-purple-500/15 transition-colors">
                        <LayoutDashboard size={15} /> My Admin
                      </button>
                    )}
                    {isStudent && (
                      <button onClick={() => navigate('/student-profile')}
                        className="w-full flex items-center gap-2 px-4 py-3 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
                        <UserCircle size={15} /> My Profile
                      </button>
                    )}
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 bg-red-500/8 border border-red-500/20 text-red-400 text-sm font-bold rounded-xl hover:bg-red-500/15 transition-colors">
                      <LogOut size={15} /> Logout
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

    </>
  );
};

export default Navbar;
